// Sección ASISTENCIA (pasar lista) · extraída 1:1 del diseño aprobado por Ann
// (docs/mockups/control_asistencia_maqueta_A.html, PR #776).
//
// Se extrae como componente PRESENTACIONAL reutilizable para poder montarlo:
//   1) dentro de ControlAsistenciaPage (entrada single-door desde el CRM, con
//      su gate al permiso `asistencia.control.pasar-lista`), y
//   2) dentro de la Vista en sala (sesión de técnico, puerta 2) como la
//      sección "ASISTENCIA".
//
// El componente asume que QUIEN LO MONTA ya decidió que está autorizado (la
// puerta/gate vive fuera). No toca AuthContext ni el motor de permisos.
// Datos por la capa aislada api/controlAsistencia.js (frontera con el backend
// del Guardián; demo etiquetada hasta que publique los endpoints).

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Stethoscope, Clock, FileText, MessageCircle, AlertTriangle, BellRing } from "lucide-react";
import { cargarDia, marcarAsistencia, buscarWalkIn, registrarWalkIn, advertirBaja,
  chatNoLeidosResumenSala, chatMensajes, chatMarcarLeidoSala } from "../../api/controlAsistencia";
import "../control-asistencia.css";

const dosDigitos = (n) => String(n).padStart(2, "0");
const isoHoy = () => { const d = new Date(); return `${d.getFullYear()}-${dosDigitos(d.getMonth() + 1)}-${dosDigitos(d.getDate())}`; };
// Franja "en curso": la última cuya hora de inicio menos 5' ya pasó (a las 11:55
// saltamos a las 12:00). Si aún no empezó ninguna, la primera.
const ANTELACION_MIN = 5;
const slotActivo = (horas, now = new Date()) => {
  if (!horas || !horas.length) return null;
  const ahora = now.getHours() * 60 + now.getMinutes();
  let activa = horas[0];
  for (const h of horas) {
    const [hh, mm] = h.split(":").map(Number);
    if (ahora >= hh * 60 + mm - ANTELACION_MIN) activa = h;
  }
  return activa;
};

const SEDES = ["MUNTANER", "MEFIR"];
// Color de la sede activa (tokens.css). Fija --sede-active para HOY, pills y "+ añadir".
const SEDE_COLOR = { MUNTANER: "#14b8a6", MEFIR: "#0e506b" };

const iniciales = (nombre = "") => {
  const p = nombre.trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase();
};

// `sedeFija` (opcional): cuando la sala lo fuerza (puerta 1), oculta el selector.
// `maqueta` (opcional): revision de estilo sin backend (datos de ejemplo etiquetados).
const PasarLista = ({ sedeFija = null, maqueta = false }) => {
  const [sede, setSede] = useState(sedeFija || SEDES[0]);
  const [dias, setDias] = useState([]);
  const [diaIdx, setDiaIdx] = useState(0);
  const [horas, setHoras] = useState([]);
  const [horasSuave, setHorasSuave] = useState([]); // horas con intensidad suave (pill menta)
  const [horaSel, setHoraSel] = useState(null);
  const [slots, setSlots] = useState({});
  const [pendiente, setPendiente] = useState(false);
  const [loading, setLoading] = useState(false);

  const [foto, setFoto] = useState(null);
  const [buscadorOpen, setBuscadorOpen] = useState(false);
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null); // cliente elegido, pendiente de confirmar
  const [avisado, setAvisado] = useState(false);          // aviso de baja enviado a recepción
  const [confirmandoBaja, setConfirmandoBaja] = useState(false); // paso de confirmación de la alerta
  const [reloj, setReloj] = useState("--:--");
  const [ficha, setFicha] = useState(null);   // cliente cuya Ficha MPD se muestra
  const [chat, setChat] = useState(null);     // cliente cuyo chat con el terapeuta se muestra
  const [chatMsgs, setChatMsgs] = useState([]); // mensajes reales del chat abierto
  const [chatNoLeidos, setChatNoLeidos] = useState({}); // {paciente_id: nº} para el bocadillo
  const [walkInAviso, setWalkInAviso] = useState(""); // aviso si NO se pudo persistir el adicional

  const fechaActual = dias[diaIdx]?.fecha;

  const cargar = useCallback(async (fecha) => {
    setLoading(true);
    try {
      const data = await cargarDia({ fecha, sede, maqueta });
      const ds = data.dias || [];
      setDias(ds);
      setHoras(data.horas || []);
      setHorasSuave(data.horas_suave || []);
      setSlots(data.slots || {});
      setPendiente(!!data.pendiente);
      // Sincronizar el día mostrado con la fecha realmente cargada (cabecera = datos).
      if (data.fecha) {
        const idx = ds.findIndex((d) => d.fecha === data.fecha);
        if (idx >= 0) setDiaIdx(idx);
      }
      setHoraSel((prev) => {
        const hs = data.horas || [];
        if (prev && hs.includes(prev)) return prev;
        // Si es HOY, abrir en la franja en curso; otro día, la primera.
        return (data.fecha === isoHoy() ? slotActivo(hs) : hs[0]) || null;
      });
    } finally {
      setLoading(false);
    }
  }, [sede, maqueta]);

  // Primera carga: hoy; pero si es fin de semana (no hay clase grupal) abre el LUNES
  // de esa semana para no mostrar la pantalla vacía.
  // eslint-disable-next-line
  useEffect(() => {
    const hoy = new Date();
    const wd = hoy.getDay(); // 0=Dom .. 6=Sáb
    const salto = wd === 0 ? 1 : wd === 6 ? 2 : 0; // Dom→Lun, Sáb→Lun
    const target = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + salto);
    const iso = `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, "0")}-${String(target.getDate()).padStart(2, "0")}`;
    cargar(iso);
  }, [sede]);

  // Reloj de sala (hora en curso).
  useEffect(() => {
    const tic = () => { const d = new Date(); setReloj(`${dosDigitos(d.getHours())}:${dosDigitos(d.getMinutes())}`); };
    tic();
    const id = setInterval(tic, 1000);
    return () => clearInterval(id);
  }, []);

  // Bocadillo de chat: nº de mensajes NO leídos por paciente (lado sala), refrescado
  // cada 20s. Si no hay permiso/endpoint → mapa vacío (no rompe, no enciende dot).
  useEffect(() => {
    if (maqueta) return;
    let vivo = true;
    const refrescar = async () => { const m = await chatNoLeidosResumenSala(); if (vivo) setChatNoLeidos(m || {}); };
    refrescar();
    const id = setInterval(refrescar, 20000);
    return () => { vivo = false; clearInterval(id); };
  }, [maqueta]);

  // Abrir el chat de un paciente: carga mensajes reales + marca leídos (apaga el bocadillo).
  const abrirChat = useCallback(async (p) => {
    setChat(p);
    setChatMsgs([]);
    const msgs = await chatMensajes(p.id);
    setChatMsgs(msgs);
    await chatMarcarLeidoSala(p.id);
    setChatNoLeidos((prev) => { const n = { ...prev }; delete n[p.id]; return n; });
  }, []);

  // Salto automático de franja: 5' antes de la siguiente clase, la pantalla cambia
  // sola a esa franja. Solo cuando se está viendo HOY (no al revisar otros días).
  const autoFranjaRef = useRef(null);
  useEffect(() => {
    const fechaCargada = dias[diaIdx]?.fecha;
    if (!fechaCargada || fechaCargada !== isoHoy() || !horas.length) return;
    autoFranjaRef.current = slotActivo(horas); // base: no salta de golpe
    const id = setInterval(() => {
      const activa = slotActivo(horas);
      if (activa && activa !== autoFranjaRef.current) {
        autoFranjaRef.current = activa;
        setHoraSel(activa);
      }
    }, 15000);
    return () => clearInterval(id);
  }, [horas, diaIdx, dias]);

  // Orden: normales primero, luego los CAMBIOS, y al FINAL los adicionales (walk-in).
  const rango = (p) => (p.extra ? 2 : p.cambio ? 1 : 0);
  const listaCruda = slots[horaSel] || slots.default || [];
  const lista = [...listaCruda].sort((a, b) => rango(a) - rango(b));
  const asistidos = lista.filter((p) => p.asistio).length;

  const cambiaDia = (d) => {
    const next = Math.max(0, Math.min(dias.length - 1, diaIdx + d));
    setDiaIdx(next);
    cargar(dias[next]?.fecha);
  };
  const irHoy = () => { setDiaIdx(0); cargar(dias[0]?.fecha); };

  const toggleAsistencia = async (p) => {
    const nuevo = !p.asistio;
    setSlots((prev) => {
      const arr = (prev[horaSel] || prev.default || []).map((x) =>
        x.id === p.id ? { ...x, asistio: nuevo } : x);
      return { ...prev, [horaSel]: arr };
    });
    if (maqueta) return;
    try {
      await marcarAsistencia({ fecha: fechaActual, hora: horaSel, cliente_id: p.id, asistio: nuevo, sede });
    } catch {
      setSlots((prev) => {
        const arr = (prev[horaSel] || []).map((x) =>
          x.id === p.id ? { ...x, asistio: p.asistio } : x);
        return { ...prev, [horaSel]: arr };
      });
    }
  };

  const toggleSalud = (id) => {
    setSlots((prev) => {
      const arr = (prev[horaSel] || prev.default || []).map((x) =>
        x.id === id && x.salud ? { ...x, salud: { ...x.salud, abierta: !x.salud.abierta } } : x);
      return { ...prev, [horaSel]: arr };
    });
  };

  const toggleAviso = (id) => {
    setSlots((prev) => {
      const arr = (prev[horaSel] || prev.default || []).map((x) =>
        x.id === id && x.aviso ? { ...x, aviso: { ...x.aviso, abierta: !x.aviso.abierta } } : x);
      return { ...prev, [horaSel]: arr };
    });
  };

  const onBuscar = async (texto) => {
    setQ(texto);
    setSeleccionado(null); setAvisado(false); setConfirmandoBaja(false);
    if (texto.trim().length < 3) { setResultados([]); return; }
    setResultados(await buscarWalkIn(texto.trim(), { maqueta }));
  };

  const cerrarBuscador = () => {
    setBuscadorOpen(false);
    setQ(""); setResultados([]); setSeleccionado(null); setAvisado(false); setConfirmandoBaja(false);
  };

  // Cliente DE BAJA que intenta entrar: NO se añade; alerta ROJA a recepción (notif grave).
  const onAdvertirBaja = async () => {
    const cli = seleccionado;
    if (!cli) return;
    if (!maqueta) {
      try { await advertirBaja({ cliente_id: cli.id, fecha: fechaActual, hora: horaSel, sede }); } catch { /* noop */ }
    }
    setConfirmandoBaja(false);
    setAvisado(true);
  };

  // Quitar un adicional añadido por error (solo filas extra/walk-in).
  const quitarWalkIn = (p) => {
    setSlots((prev) => {
      const arr = (prev[horaSel] || prev.default || []).filter((x) => x.id !== p.id);
      return { ...prev, [horaSel]: arr };
    });
  };

  // Paso 2: confirmar (OK) el cliente seleccionado -> se registra como adicional.
  const confirmarWalkIn = async () => {
    const cli = seleccionado;
    if (!cli) return;
    if (cli.es_baja) return;   // cliente de baja NO entra (se usa "Advertir")
    setSlots((prev) => {
      const arr = [...(prev[horaSel] || prev.default || [])];
      const ya = arr.find((p) => p.id === cli.id || p.nombre.toLowerCase() === cli.nombre.toLowerCase());
      if (ya) { ya.asistio = true; ya.extra = true; }
      else { arr.push({ id: cli.id, nombre: cli.nombre, asistio: true, extra: true }); }
      return { ...prev, [horaSel]: arr };
    });
    cerrarBuscador();
    // Anota la CLASE ADICIONAL en la ficha del cliente (asistencia extra, es_extra).
    // En maqueta no hay backend. Si falla, se AVISA (no se finge un registro correcto).
    if (!maqueta) {
      try {
        await registrarWalkIn({ fecha: fechaActual, hora: horaSel, cliente_id: cli.id, sede });
        setWalkInAviso("");
      } catch {
        setWalkInAviso(`No se pudo registrar la clase adicional de ${cli.nombre} en su ficha (backend de sala pendiente). Avisa al responsable.`);
      }
    }
  };

  return (
    <div style={{ "--sede-active": SEDE_COLOR[sede] || SEDE_COLOR.MUNTANER }}>
      {/* Cabecera */}
      <div className="ca-cab">
        <div className="ca-cab-left">
          <h1>CONTROL DE ASISTENCIA</h1>
          <span className="ca-reloj" title="Hora de sala">
            <Clock size={18} strokeWidth={2} />
            <b>{reloj}</b>
          </span>
        </div>
        <div className="ca-nav-dia">
          <button className="ca-btn-nav" onClick={() => cambiaDia(-1)}>‹</button>
          <span className="ca-fecha">{dias[diaIdx]?.label || "—"}</span>
          <button className="ca-btn-nav" onClick={() => cambiaDia(1)}>›</button>
          <button className="ca-btn-hoy" onClick={irHoy}>HOY</button>
        </div>
      </div>

      {/* Sedes (oculto si la sala fuerza la sede) */}
      {!sedeFija && (
        <div className="ca-sedes">
          {SEDES.map((s) => (
            <button key={s} data-sede={s} className={`ca-sede ${s === sede ? "activa" : ""}`} onClick={() => setSede(s)}>{s}</button>
          ))}
        </div>
      )}

      {/* Franjas horarias */}
      <div className="ca-horas">
        {horas.map((h) => (
          <button
            key={h}
            className={`ca-pill ${h === horaSel ? "activa" : ""} ${horasSuave.includes(h) ? "crm-estado-asistencia--suave" : ""}`}
            onClick={() => setHoraSel(h)}
          >{h}</button>
        ))}
      </div>

      <div className="ca-slot-info">
        <span><b>{sede}</b> · <b>{horaSel || "—"}</b></span>
        <span>{loading ? "Cargando…" : `${asistidos}/${lista.length} asistentes`}</span>
      </div>

      {maqueta && (
        <div className="ca-demo-banner">
          MAQUETA · datos de ejemplo para revisar el estilo · sin backend.
        </div>
      )}
      {!maqueta && pendiente && (
        <div className="ca-demo-banner">
          Listado pendiente · el endpoint de asistencia lo publica el Guardian.
        </div>
      )}

      {/* Listado */}
      <div className="ca-listado">
        {lista.map((p) => (
          <div key={p.id} className={`ca-fila ${p.extra ? "extra" : p.cambio ? "cambio" : p.deuda ? "deuda" : ""} ${p.asistio ? "ok" : ""}`}>
            <div className="ca-fila-main">
              <div className="ca-nombre-row">
                <span
                  className={`ca-avatar ${p.foto ? "clickable" : ""}`}
                  onClick={p.foto ? () => setFoto({ src: p.foto, nombre: p.nombre }) : undefined}
                >
                  {p.foto ? <img src={p.foto} alt="" /> : iniciales(p.nombre)}
                </span>
                <span className="ca-nombre-wrap">
                  <span className="ca-nombre">
                    {p.nombre}
                    {p.es_monitorizado && (
                      <span className="ca-monitor" title="Paciente monitorizado · seguimiento sin tratamiento">
                        <Stethoscope size={13} strokeWidth={2} />
                      </span>
                    )}
                    {p.cambio && (
                      <>
                        <span className="ca-tag cambio">CAMBIO</span>
                        {p.suave && <span className="ca-tag-suave">SUAVE</span>}
                        <span className="ca-habitual">(habitual: {p.habitual})</span>
                      </>
                    )}
                  </span>
                  {p.extra && <span className="ca-tag extra">CLASE ADICIONAL</span>}
                </span>
              </div>
              {p.salud && (
                <div className={`ca-salud ${p.salud.abierta ? "open" : ""}`}>
                  <div className="ca-salud-head" onClick={() => toggleSalud(p.id)}>
                    <span className="ca-tit">{p.salud.autor}</span>
                    <span className="ca-chev">›</span>
                  </div>
                  <div className="ca-salud-body">
                    <div className="ca-nota">{p.salud.nota}</div>
                  </div>
                </div>
              )}
              {p.aviso && (
                <div className={`ca-aviso ${p.aviso.abierta ? "open" : ""}`} onClick={() => toggleAviso(p.id)}>
                  <AlertTriangle className="ca-aviso-ico" size={18} strokeWidth={2} />
                  <span className="ca-aviso-txt"><b>Aviso administrativo:</b> {p.aviso.nota}</span>
                </div>
              )}
            </div>
            <div className="ca-fila-acciones">
              {p.tiene_ficha_mpd && (
                <button className="ca-btn-ficha" title="Ficha MPD compartida (el fisio ha anotado algo)" onClick={() => setFicha(p)}>
                  <FileText size={20} strokeWidth={2} />
                </button>
              )}
              {p.paciente_fisio && (
                <button className="ca-btn-chat" title="Chat con el terapeuta" onClick={() => abrirChat(p)}>
                  <MessageCircle size={20} strokeWidth={2} />
                  {chatNoLeidos[p.id] > 0 && <span className="ca-chat-dot" />}
                </button>
              )}
              {p.extra && (
                <button className="ca-btn-quitar" title="Quitar de la lista" onClick={() => quitarWalkIn(p)}>×</button>
              )}
              <button className="ca-btn-asist" onClick={() => toggleAsistencia(p)}>
                {p.asistio ? "Asiste" : "Marcar"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="ca-add">
        <button className="ca-btn-add" onClick={() => setBuscadorOpen(true)}>+ Añadir asistente sin reserva</button>
        {walkInAviso && <div className="ca-walkin-aviso">{walkInAviso}</div>}
      </div>

      {/* Lightbox foto */}
      <div className={`ca-foto-overlay ${foto ? "open" : ""}`} onClick={() => setFoto(null)}>
        {foto && <img src={foto.src} alt="" />}
        {foto && <span className="ca-nom">{foto.nombre}</span>}
      </div>

      {/* Modal buscador walk-in */}
      <div className={`ca-overlay ${buscadorOpen ? "open" : ""}`}>
        <div className="ca-modal">
          <h2>Añadir asistente sin reserva</h2>
          <input
            className="ca-buscador"
            placeholder="Escribe el nombre (≥3 letras)…"
            value={q}
            onChange={(e) => onBuscar(e.target.value)}
            autoFocus
          />
          <div className="ca-resultados">
            {q.trim().length < 3 ? (
              <div className="ca-hint">Escribe al menos 3 letras…</div>
            ) : resultados.length ? (
              resultados.map((c) => (
                <div
                  key={c.id}
                  className={`ca-res ${seleccionado?.id === c.id ? "sel" : ""} ${c.es_baja ? "baja" : ""}`}
                  onClick={() => { setSeleccionado(c); setAvisado(false); }}
                >
                  {c.nombre}
                  {c.es_baja && <span className="ca-tag-baja">BAJA</span>}
                </div>
              ))
            ) : (
              <div className="ca-hint">Sin resultados.</div>
            )}
          </div>
          {seleccionado && !seleccionado.es_baja && (
            <div className="ca-sel-confirm">
              <span>Seleccionado: <b>{seleccionado.nombre}</b></span>
              <button className="ca-btn-ok" onClick={confirmarWalkIn}>OK</button>
            </div>
          )}
          {seleccionado && seleccionado.es_baja && (
            <div className="ca-sel-baja">
              <div className="ca-sel-baja-txt">
                <b>{seleccionado.nombre}</b> está <b>DE BAJA</b> · no puede entrar.
                Debe pasar por recepción y pagar.
              </div>
              {avisado ? (
                <span className="ca-aviso-ok">Alerta enviada a recepción</span>
              ) : confirmandoBaja ? (
                <div className="ca-baja-confirm">
                  <span>¿Mandar alerta al editor (recepción)?</span>
                  <button className="ca-btn-advertir" onClick={onAdvertirBaja}>Sí</button>
                  <button className="ca-cerrar ca-btn-no" onClick={() => setConfirmandoBaja(false)}>No</button>
                </div>
              ) : (
                <button
                  className="ca-campana-roja"
                  title="Mandar alerta a recepción (acceso de cliente de baja)"
                  onClick={() => setConfirmandoBaja(true)}
                >
                  <BellRing size={20} strokeWidth={2.2} />
                </button>
              )}
            </div>
          )}
          <button className="ca-cerrar" onClick={cerrarBuscador}>Cerrar</button>
        </div>
      </div>

      {/* Modal Ficha MPD compartida (solo lectura · sin datos clínicos) */}
      <div className={`ca-overlay ${ficha ? "open" : ""}`}>
        <div className="ca-chat" style={{ maxWidth: 560 }}>
          <div className="ca-chat-head ficha">
            <div>
              <div className="ca-chat-tit">Ficha MPD compartida</div>
              <div className="ca-chat-sub">{ficha?.nombre}</div>
            </div>
            <button className="ca-chat-x" onClick={() => setFicha(null)} title="Cerrar">×</button>
          </div>
          <div className="ca-chat-demo">Solo lectura desde el kiosko · campos compartidos con fisio · NO incluye datos clínicos.</div>
          <div className="ca-chat-cuerpo" style={{ gap: 16 }}>
            <div className="ca-ficha-campo aviso">
              <label>Advertencia corta (aparece en el listado)</label>
              <div className="ca-ficha-valor">{ficha?.ficha_mpd?.advertencia_corta || "—"}</div>
            </div>
            <div className="ca-ficha-campo">
              <label>Adaptaciones MPD</label>
              <div className="ca-ficha-valor">{ficha?.ficha_mpd?.adaptaciones_mpd || "—"}</div>
            </div>
            <div className="ca-ficha-campo">
              <label>Anotaciones para el técnico</label>
              {(ficha?.ficha_mpd?.anotaciones || []).length ? (
                (ficha.ficha_mpd.anotaciones).map((a, i) => (
                  <div key={i} className="ca-ficha-anot"><b>{a.autor}</b> · {a.fecha}<br />{a.texto}</div>
                ))
              ) : <div className="ca-hint">Sin anotaciones.</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Chat con el terapeuta (solo lectura desde el kiosko) */}
      <div className={`ca-overlay ${chat ? "open" : ""}`}>
        <div className="ca-chat">
          <div className="ca-chat-head">
            <div>
              <div className="ca-chat-tit">Chat con el terapeuta</div>
              <div className="ca-chat-sub">{chat?.nombre}</div>
            </div>
            <button className="ca-chat-x" onClick={() => setChat(null)} title="Cerrar">×</button>
          </div>
          <div className="ca-chat-cuerpo">
            {chatMsgs.length ? (
              chatMsgs.map((m) => {
                const esSala = m.lado === "sala";
                // Chat interno: solo el nombre de pila, sin apellidos.
                const nombre = (m.autor_nombre || "").trim().split(/\s+/)[0] || (esSala ? "Sala" : "Fisio");
                const ts = m.created_at ? new Date(m.created_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
                return (
                  <div key={m.id} className={`ca-msg ${esSala ? "propio" : "ajeno"}`}>
                    <div className="ca-msg-autor"><b>{nombre}</b>{ts ? ` · ${ts}` : ""}</div>
                    <div className="ca-msg-burbuja">{m.texto}</div>
                  </div>
                );
              })
            ) : <div className="ca-hint">Aún no hay mensajes.</div>}
          </div>
          <div className="ca-chat-pie-nota">Solo lectura desde el kiosko · responder se hace desde la cuenta nominal en la ficha.</div>
        </div>
      </div>
    </div>
  );
};

export default PasarLista;
